import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { Headline1, Meta, theme } from 'aimo-ui';
import EmptyView from 'components/account/companyBenefits/EmptyView';
import CrickleCutCardBackground from 'components/common/CrickleCutCardBackground';
import WaitingNumber from 'components/longTermParking/waitingList/WaitingNumber';
import AccountContext from 'context/AccountContext';
import useGetMatchingPermitsForParkingZone from 'hooks/useGetMatchingPermitsForParkingZone';
import usePollSalesOrderStatus from 'hooks/usePollSalesOrderStatus';
import { camelCase } from 'lodash';
import { default as React, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView } from 'react-native-gesture-handler';
import LoyaltyPermitTicket from 'screens/permit/loyalty/LoyaltyPermitTicket';
import { usePermitStore } from 'stores/permitStore';
import styled from 'styled-components/native';
import { SheetDetailsWrapper, StyledWrapper } from 'styles/ContainerStyles';
import {
  CompanyBenefit,
  SalesOrderStatus,
  UnifiedPermit,
  UnifiedPermitType,
} from 'types/generatedSchemaTypes';
import { RootNavProps, RootStackParamList } from 'types/navigation';
import { isWeb } from 'utils/deviceUtils';
import {
  getPermitName,
  handlePermitNavigation,
  isItemAccessPermit,
  isItemMgLtpAccessPermit,
  isItemSalesOrder,
  isWaitingListItem,
  sortPermitsByWaitingAction,
} from 'utils/permitUtils';
import ActivateSalesOrderPermitButton from './ActivateSalesOrderPermitButton';
import { ActivateInfoBox, PaymentErrorInfoBox } from './PermitInfoBoxes';
import PermitTicketContent from './PermitTicketContent';
import RedeemPermitButton from './RedeemPermitButton';
import ResolvePaymentButton from './ResolvePaymentButton';
import { SalesOrderReceipt } from './SalesOrderReceiptClass';

const PermitWrapper = styled.TouchableOpacity`
  margin-bottom: 20px;
`;

const PermitTitleContainer = styled.View`
  width: 100%;
  padding: 15px 20px;
`;

const PermitItemTitle = ({
  permit,
  benefits,
}: {
  permit: UnifiedPermit;
  benefits?: CompanyBenefit[];
}) => {
  const { t } = useTranslation();
  const {
    id,
    orderLines,
    isWaitingActivation,
    permitType,
    positionInWaitingList,
    orderStatus,
  } = permit;

  let metaText = '';
  if (isItemAccessPermit(permit)) {
    metaText = t('permit.access.name');
  } else if (isItemMgLtpAccessPermit(permit)) {
    const permitTypeName = t(
      `permit.${camelCase(permit?.permitType?.toString())}.name`,
      ''
    );
    metaText = permit?.quantity
      ? permit?.quantity + '\u00D7' + permitTypeName
      : permitTypeName;
  } else {
    metaText = orderLines?.[0]?.quantity
      ? `${orderLines?.[0]?.quantity} \u00D7 ${t('services.permitParking')}`
      : t('services.permitParking');
  }

  const salesOrderReceipt = isItemSalesOrder(permit)
    ? new SalesOrderReceipt(permit)
    : null;

  return (
    <PermitTitleContainer>
      <StyledWrapper center mb={5}>
        <Headline1 testID={`permit-${id}-name`}>
          {getPermitName(permit, benefits)}
        </Headline1>
      </StyledWrapper>
      <StyledWrapper center>
        <Meta color={theme.colors.gray300}>{metaText}</Meta>
      </StyledWrapper>

      {(permitType === 'WAITING_LIST' ||
        orderStatus === 'WAITING_FOR_CSR_APPROVAL') && (
        <StyledWrapper>
          <WaitingNumber
            isSalesOrder={permitType === 'SALES_ORDER'}
            orderStatus={orderStatus ?? 'WAITING_FOR_ACTIVATION'}
            waitingCount={positionInWaitingList}
          />
        </StyledWrapper>
      )}

      <PaymentErrorInfoBox salesOrderReceipt={salesOrderReceipt} />

      <ResolvePaymentButton permit={permit} />

      {salesOrderReceipt?.isInProgress && (
        <ActivateInfoBox salesOrderReceipt={salesOrderReceipt} />
      )}

      {salesOrderReceipt?.isWaiting && (
        <ActivateSalesOrderPermitButton permit={permit} />
      )}

      {isWaitingActivation &&
        !isItemSalesOrder(permit) &&
        !isWaitingListItem(permit) && <RedeemPermitButton permit={permit} />}
    </PermitTitleContainer>
  );
};

const PermitItem = ({
  permit,
  cutBackgroundColor,
}: {
  permit: UnifiedPermit;
  cutBackgroundColor?: string;
}) => {
  const { navigate } = useNavigation<RootNavProps>();
  const salesOrder = isItemSalesOrder(permit) ? permit : undefined;

  usePollSalesOrderStatus(
    salesOrder?.id,
    (permit.orderStatus as SalesOrderStatus) ?? 'ACTIVE'
  );

  const handleClick = () => {
    usePermitStore
      .getState()
      .setSelectedPermit(permit.orderLineIdWithVersion ?? permit?.id ?? '');
    if (permit.permitType === 'SALES_ORDER') {
      return navigate('salesOrderPermitDetails', {
        orderLineIdWithVersion: permit.orderLineIdWithVersion ?? '',
      });
    }
    if (permit.permitType === 'WAITING_LIST') {
      return navigate('longTermParking', {
        screen: 'waitingListPermitDetails',
        params: { id: permit.id },
      });
    }
    handlePermitNavigation(permit, navigate);
  };

  return (
    <PermitWrapper onPress={handleClick} activeOpacity={0.65}>
      <CrickleCutCardBackground
        aboveCutView={<PermitItemTitle permit={permit} />}
        belowCutView={
          <PermitTicketContent permit={permit} onPress={handleClick} />
        }
        backgroundColor={theme.colors.white}
        cutColor={cutBackgroundColor ?? theme.colors.gray100}
      />
    </PermitWrapper>
  );
};
const Permits: React.FC = () => {
  const { setOptions: setNavigationOptions } = useNavigation<RootNavProps>();
  const { t } = useTranslation();
  const {
    customer: { permits },
  } = useContext(AccountContext);
  const { params } = useRoute<RouteProp<RootStackParamList, 'permits'>>();
  const { licensePlate, cutBackgroundColor, permitType, parkZoneUid } =
    params ?? {};

  const { matchingPermits } = useGetMatchingPermitsForParkingZone(
    parkZoneUid,
    permitType ? [permitType as UnifiedPermitType] : []
  );

  const targetPermits = matchingPermits?.length ? matchingPermits : permits;

  const filteredPermits = sortPermitsByWaitingAction(
    licensePlate
      ? targetPermits?.filter((permit) =>
          permit?.registrationPlates?.find(
            (plate) => plate?.plateText === licensePlate
          )
        )
      : targetPermits?.filter((permit) => {
          if (typeof permitType === 'string') {
            return permit.permitType === permitType;
          }
          if (Array.isArray(permitType) && permitType.length) {
            return permitType.includes(permit.permitType as UnifiedPermitType);
          }
          return true;
        })
  );

  useEffect(() => {
    setNavigationOptions({
      title:
        permitType === 'LOYALTY'
          ? t('services.loyaltyPermits')
          : t('services.permits'),
      cardStyle: { backgroundColor: theme.colors.gray100 },
    });
  }, [t, setNavigationOptions, permitType]);

  return (
    <ScrollView>
      <SheetDetailsWrapper>
        <StyledWrapper
          center={isWeb}
          mb={10}
          maxWidth={isWeb ? 600 : undefined}>
          {filteredPermits?.length ? (
            filteredPermits?.map((permit) => {
              if (permit.permitType === 'LOYALTY') {
                return (
                  <LoyaltyPermitTicket
                    permit={permit}
                    key={permit?.id}
                    cutBackgroundColor={cutBackgroundColor}
                  />
                );
              }
              return (
                <PermitItem
                  permit={permit}
                  key={permit?.orderLineIdWithVersion || permit?.id}
                  cutBackgroundColor={cutBackgroundColor}
                />
              );
            })
          ) : (
            <EmptyView LTP />
          )}
        </StyledWrapper>
      </SheetDetailsWrapper>
    </ScrollView>
  );
};

export default Permits;
